import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { DISPATCH_TICKET_TYPES } from 'utils/constants'
import { AssetResponseSchema } from 'gen/rtk-openapi.ts'

export interface UIAssetModel extends AssetResponseSchema {
  asset?: { id?: string }
  assetId?: string
}

// Define the state interface
interface DispatchState {
  status: string
  pickupAssets: UIAssetModel[]
  dropoffAssets: UIAssetModel[]
  deliveryAssets: UIAssetModel[]
  pickAndReturnAssets: UIAssetModel[]
  liveLoadAssets: UIAssetModel[]
  initialPickupAssets: UIAssetModel[]
  initialDropoffAssets: UIAssetModel[]
  initialPickAndReturnAssets: UIAssetModel[]
  initialDeliveryAssets: UIAssetModel[]
  initialLiveLoadAssets: UIAssetModel[]
}

// Define the payload interface for setting assets
interface SetAssetsPayload {
  ticketType: string
  assets: any[] // Specify the type of assets if possible
}

const initialState: DispatchState = {
  status: '',
  pickupAssets: [],
  dropoffAssets: [],
  pickAndReturnAssets: [],
  deliveryAssets: [],
  liveLoadAssets: [],
  initialPickupAssets: [],
  initialDropoffAssets: [],
  initialPickAndReturnAssets: [],
  initialDeliveryAssets: [],
  initialLiveLoadAssets: [],
}
const ticketTypeMap = {
  [DISPATCH_TICKET_TYPES.PICK_UP.id]: 'pickupAssets',
  [DISPATCH_TICKET_TYPES.DROP_OFF.id]: 'dropoffAssets',
  [DISPATCH_TICKET_TYPES.PICK_AND_RETURN.id]: 'pickAndReturnAssets',
  [DISPATCH_TICKET_TYPES.DELIVERY.id]: 'deliveryAssets',
  [DISPATCH_TICKET_TYPES.LIVE_LOAD.id]: 'liveLoadAssets',
}
const ticketTypeInitialMap = {
  [DISPATCH_TICKET_TYPES.PICK_UP.id]: 'initialPickupAssets',
  [DISPATCH_TICKET_TYPES.DROP_OFF.id]: 'initialDropoffAssets',
  [DISPATCH_TICKET_TYPES.PICK_AND_RETURN.id]: 'initialPickAndReturnAssets',
  [DISPATCH_TICKET_TYPES.DELIVERY.id]: 'initialDeliveryAssets',
  [DISPATCH_TICKET_TYPES.LIVE_LOAD.id]: 'initialLiveLoadAssets',
}

export const dispatchSlice = createSlice({
  name: 'dispatch',
  initialState,
  reducers: {
    setAssets(state, action: PayloadAction<SetAssetsPayload>) {
      const { ticketType, assets } = action.payload
      const stateKey = ticketTypeMap[ticketType]
      if (stateKey === undefined) {
        console.error('cannot set REDUX state with action.payload' + JSON.stringify(action.payload))
      }
      state[stateKey] = assets
    },
    setInitialAssets(state, action: PayloadAction<SetAssetsPayload>) {
      const { ticketType, assets } = action.payload
      const initialStateKey = ticketTypeInitialMap[ticketType]
      const stateKey = ticketTypeMap[ticketType]
      if (stateKey === undefined || initialStateKey === undefined) {
        console.error('cannot set REDUX state with action.payload' + JSON.stringify(action.payload))
      }
      // set both initial and current state
      state[initialStateKey] = assets
      state[stateKey] = assets
    },
    resetAssets(state) {
      // set current state but leave initial intact
      Object.keys(ticketTypeMap).forEach(ticketTypeId => {
        state[ticketTypeMap[ticketTypeId]] = []
      })
    },
  },
})

export const { setAssets, setInitialAssets, resetAssets } = dispatchSlice.actions

export default dispatchSlice.reducer
